import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as KeyboardEventReact, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router'
import { useSesion } from '../auth/useSesion'
import { useIntegraciones } from '../hooks/useIntegraciones'
import { esAdmin, useUsuarioActual } from '../hooks/useUsuarioActual'
import { ConmutadorTema } from '../tema/ConmutadorTema'
import './AppShell.css'
import { IconoAbrirPanel, IconoCerrar, IconoMas, IconoSalir, IconoUsuarios } from './iconos'
import { LogisticaIconoTema } from './marca/LogisticaIcono'
import {
  DESTINOS,
  GRUPOS_BARRA_MOVIL,
  INICIO,
  destinosVisibles,
  grupoDeRuta,
  gruposVisibles,
  rutaCoincide,
  type Destino,
  type IdGrupo,
} from './navegacion'

/**
 * Estructura de la aplicacion con el menu agrupado (variante B del mockup
 * `trazabis/sketches/menu-agrupado`, aprobada).
 *
 * Escritorio (>= 901 px): barra lateral de 240 px con «Logística» suelta arriba
 * y una entrada por grupo. Cada grupo abre un panel flotante a su derecha al
 * pasar el raton o al hacer clic; el grupo de la pantalla abierta la muestra
 * debajo de su nombre. La cuenta (correo, tema y cerrar sesion) se recoge en un
 * unico boton al pie con su propio panel.
 *
 * Movil: barra inferior de cinco celdas (inicio, tres grupos y «Más») que
 * abren una hoja inferior. Con diecinueve destinos la barra anterior se
 * desplazaba en horizontal y escondia la mitad.
 *
 * Accesibilidad: los botones de grupo llevan `aria-expanded` y
 * `aria-controls`; Enter, Espacio o la flecha abajo abren y llevan el foco a
 * la primera pantalla; las flechas (e Inicio/Fin) recorren el panel; Escape
 * cierra y devuelve el foco al boton. Las hojas son dialogos modales con el
 * foco atrapado. Objetivos tactiles de 44 px o mas.
 *
 * Trazabis DS: paneles y hojas planos, con borde y sin sombra. Los colores
 * salen de tokens (`--tz-*` en `AppShell.css`, `bg-superficie`... en Tailwind),
 * asi que los dos temas se resuelven solos.
 */

interface Props {
  children: ReactNode
}

type IdPanel = IdGrupo | 'cuenta'
type IdHoja = IdGrupo | 'mas'
type Abierto = { id: IdPanel; fijado: boolean; enfocar?: 'primero' | 'ultimo' }

const ID_HOJA = 'hoja-movil'
const idPanel = (id: IdPanel) => `panel-${id}`

/** Lo que se recorre con las flechas: enlaces y botones, no los radios del tema. */
function enfocables(contenedor: Element | null): HTMLElement[] {
  if (!contenedor) return []
  return [...contenedor.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')]
}

/** Flechas arriba/abajo con vuelta, Inicio y Fin. Devuelve si la tecla era suya. */
function moverConFlechas(evento: KeyboardEventReact<HTMLElement>, contenedor: Element | null): boolean {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(evento.key)) return false
  // Dentro del conmutador de tema las flechas cambian de opcion (radiogroup).
  if ((evento.target as HTMLElement).tagName === 'INPUT') return false
  const lista = enfocables(contenedor)
  if (lista.length === 0) return false
  evento.preventDefault()
  const actual = lista.indexOf(document.activeElement as HTMLElement)
  const siguiente =
    evento.key === 'Home'
      ? 0
      : evento.key === 'End'
        ? lista.length - 1
        : evento.key === 'ArrowDown'
          ? (actual + 1) % lista.length
          : (actual - 1 + lista.length) % lista.length
  lista[siguiente].focus()
  return true
}

function iniciales(texto: string): string {
  const palabras = texto.split(/[\s._-]+/).filter(Boolean)
  const letras = palabras.length > 1 ? palabras[0][0] + palabras[1][0] : texto.slice(0, 2)
  return letras.toUpperCase()
}

/** Marca del aplicativo: icono de 36 px, nombre y «Panel de operación». */
function Marca() {
  return (
    <div className="tz-brand">
      <div className="tz-brand-row">
        <LogisticaIconoTema size={36} />
        <p className="tz-brand-name">Logística</p>
      </div>
      <span className="tz-brand-sub">Panel de operación</span>
    </div>
  )
}

/** Correo, tema y cerrar sesion: lo que se hace «a la cuenta», no a los datos. */
function Cuenta({ correo, salir }: { correo: string; salir: () => void }) {
  return (
    <>
      <p className="tz-cuenta-correo" title={correo}>
        {correo}
      </p>
      <ConmutadorTema className="tz-cuenta-tema" />
      <button type="button" onClick={salir} className="tz-nav-item">
        <IconoSalir />
        <span>Cerrar sesión</span>
      </button>
    </>
  )
}

function Enlace({ destino, alElegir }: { destino: Destino; alElegir: () => void }) {
  return (
    <NavLink to={destino.ruta} className="tz-nav-item" onClick={alElegir}>
      <destino.Icono />
      <span>{destino.etiqueta}</span>
    </NavLink>
  )
}

export function AppShell({ children }: Props) {
  const { sesion, salir } = useSesion()
  const correo = sesion?.user?.email ?? ''
  const usuario = useUsuarioActual()
  const admin = esAdmin(usuario.data)
  const integraciones = useIntegraciones()
  const { pathname } = useLocation()

  const grupos = gruposVisibles(destinosVisibles(DESTINOS, { admin, integraciones: integraciones.data }))
  const grupoActivo = grupoDeRuta(pathname, grupos)
  const pantallaActiva = grupoActivo?.destinos.find((d) => rutaCoincide(pathname, d.ruta))

  const nombre = usuario.data?.nombre?.trim() || correo.split('@')[0] || 'Cuenta'
  const rol = usuario.data ? (admin ? 'Administrador' : 'Operador') : ''
  const cerrarSesion = () => void salir()

  // ---- Escritorio: paneles flotantes ----------------------------------------
  // `fijado`: abierto con clic o teclado; el que abre el raton se cierra al
  // salir de el. `enfocar`: tras pintarlo, el foco entra en el panel.
  const [abierto, setAbierto] = useState<Abierto | null>(null)
  const lateralRef = useRef<HTMLElement>(null)
  const botones = useRef(new Map<IdPanel, HTMLButtonElement>())
  const paneles = useRef(new Map<IdPanel, HTMLDivElement>())
  const temporizador = useRef<number | undefined>(undefined)

  const cerrarPanel = (devolverFoco: boolean) => {
    window.clearTimeout(temporizador.current)
    if (devolverFoco && abierto) botones.current.get(abierto.id)?.focus()
    setAbierto(null)
  }

  const abrirPanel = (id: IdPanel, fijado: boolean, enfocar?: Abierto['enfocar']) => {
    window.clearTimeout(temporizador.current)
    setAbierto((previo) => ({ id, fijado: fijado || (previo?.id === id && previo.fijado), enfocar }))
  }

  // Coloca el panel junto a su boton, dentro de la ventana, y le pasa el foco
  // si se abrio con el teclado.
  useLayoutEffect(() => {
    if (!abierto) return
    const boton = botones.current.get(abierto.id)
    const panel = paneles.current.get(abierto.id)
    const lateral = lateralRef.current
    if (!boton || !panel || !lateral) return
    const caja = boton.getBoundingClientRect()
    const alto = panel.offsetHeight
    const arriba = abierto.id === 'cuenta' ? caja.bottom - alto : caja.top - 8
    panel.style.left = `${lateral.getBoundingClientRect().right + 8}px`
    panel.style.top = `${Math.max(8, Math.min(arriba, window.innerHeight - alto - 8))}px`
    if (abierto.enfocar) {
      const lista = enfocables(panel)
      lista[abierto.enfocar === 'primero' ? 0 : lista.length - 1]?.focus()
    }
  }, [abierto])

  // Clic fuera del boton y del panel: se cierra sin mover el foco.
  useEffect(() => {
    if (!abierto) return
    const alPulsar = (evento: PointerEvent) => {
      const objetivo = evento.target as Node
      if (botones.current.get(abierto.id)?.contains(objetivo)) return
      if (paneles.current.get(abierto.id)?.contains(objetivo)) return
      setAbierto(null)
    }
    document.addEventListener('pointerdown', alPulsar)
    return () => document.removeEventListener('pointerdown', alPulsar)
  }, [abierto])

  useEffect(() => () => window.clearTimeout(temporizador.current), [])

  const salirConRaton = (evento: React.PointerEvent, id: IdPanel) => {
    if (evento.pointerType !== 'mouse') return
    window.clearTimeout(temporizador.current)
    if (abierto?.id !== id || abierto.fijado) return
    temporizador.current = window.setTimeout(() => setAbierto((previo) => (previo?.fijado ? previo : null)), 220)
  }

  // El foco que sale del boton y de su panel (Tab hacia fuera) lo cierra.
  const alPerderFoco = (evento: React.FocusEvent, id: IdPanel) => {
    const destino = evento.relatedTarget as Node | null
    if (!destino) return
    if (botones.current.get(id)?.contains(destino) || paneles.current.get(id)?.contains(destino)) return
    setAbierto((previo) => (previo?.id === id ? null : previo))
  }

  const propsBoton = (id: IdPanel) => ({
    ref: (nodo: HTMLButtonElement | null) => {
      if (nodo) botones.current.set(id, nodo)
      else botones.current.delete(id)
    },
    type: 'button' as const,
    'aria-expanded': abierto?.id === id,
    'aria-controls': idPanel(id),
    onClick: () => {
      if (abierto?.id === id && abierto.fijado) cerrarPanel(false)
      else abrirPanel(id, true)
    },
    onKeyDown: (evento: KeyboardEventReact<HTMLButtonElement>) => {
      if (['Enter', ' ', 'ArrowDown', 'ArrowRight'].includes(evento.key)) {
        evento.preventDefault()
        abrirPanel(id, true, 'primero')
      } else if (evento.key === 'ArrowUp') {
        evento.preventDefault()
        abrirPanel(id, true, 'ultimo')
      } else if (evento.key === 'Escape' && abierto?.id === id) {
        evento.preventDefault()
        cerrarPanel(true)
      }
    },
    // Espacio activa un boton al soltar la tecla: el panel ya se abrio al pulsarla.
    onKeyUp: (evento: KeyboardEventReact<HTMLButtonElement>) => {
      if (evento.key === ' ') evento.preventDefault()
    },
    onPointerEnter: (evento: React.PointerEvent) => {
      if (evento.pointerType !== 'mouse') return
      window.clearTimeout(temporizador.current)
      if (abierto?.id === id) return
      temporizador.current = window.setTimeout(() => abrirPanel(id, false), 120)
    },
    onPointerLeave: (evento: React.PointerEvent) => salirConRaton(evento, id),
  })

  const propsPanel = (id: IdPanel, etiqueta: string) => ({
    ref: (nodo: HTMLDivElement | null) => {
      if (nodo) paneles.current.set(id, nodo)
      else paneles.current.delete(id)
    },
    id: idPanel(id),
    role: 'group',
    'aria-label': etiqueta,
    hidden: abierto?.id !== id,
    className: 'tz-panel',
    onKeyDown: (evento: KeyboardEventReact<HTMLDivElement>) => {
      if (moverConFlechas(evento, evento.currentTarget)) return
      if (evento.key === 'Escape' || evento.key === 'ArrowLeft') {
        evento.preventDefault()
        cerrarPanel(true)
      }
    },
    onPointerEnter: (evento: React.PointerEvent) => {
      if (evento.pointerType === 'mouse') window.clearTimeout(temporizador.current)
    },
    onPointerLeave: (evento: React.PointerEvent) => salirConRaton(evento, id),
  })

  // ---- Movil: hoja inferior --------------------------------------------------
  const [hoja, setHoja] = useState<IdHoja | null>(null)
  const abridorHoja = useRef<HTMLElement | null>(null)
  const hojaRef = useRef<HTMLDivElement>(null)

  const abrirHoja = (id: IdHoja, abridor: HTMLElement) => {
    abridorHoja.current = abridor
    setHoja(id)
  }
  const cerrarHoja = (devolverFoco: boolean) => {
    setHoja(null)
    if (devolverFoco) abridorHoja.current?.focus()
  }

  // Al abrir, el foco va a la pantalla actual o a lo primero de la hoja.
  useEffect(() => {
    if (!hoja) return
    const caja = hojaRef.current
    const actual = caja?.querySelector<HTMLElement>('[aria-current="page"]')
    ;(actual ?? enfocables(caja)[0])?.focus()
  }, [hoja])

  const alTeclearEnHoja = (evento: KeyboardEventReact<HTMLDivElement>) => {
    if (evento.key === 'Escape') {
      evento.preventDefault()
      cerrarHoja(true)
      return
    }
    if (evento.key === 'Tab') {
      // Trampa de foco del dialogo modal (el radio marcado del tema cuenta).
      const lista = [
        ...evento.currentTarget.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:checked'),
      ]
      const primero = lista[0]
      const ultimo = lista[lista.length - 1]
      if (evento.shiftKey && document.activeElement === primero) {
        evento.preventDefault()
        ultimo.focus()
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault()
        primero.focus()
      }
      return
    }
    moverConFlechas(evento, evento.currentTarget.querySelector('nav'))
  }

  const gruposBarra = GRUPOS_BARRA_MOVIL.flatMap((id) => grupos.find((g) => g.id === id) ?? [])
  const gruposMas = grupos.filter((g) => !GRUPOS_BARRA_MOVIL.includes(g.id))
  const masActivo = gruposMas.some((g) => g === grupoActivo)
  const grupoHoja = grupos.find((g) => g.id === hoja)
  const tituloHoja = hoja === 'mas' ? 'Más' : (grupoHoja?.etiqueta ?? '')

  return (
    <div className="min-h-screen bg-lienzo text-tinta">
      {/* Escritorio: lateral fija de 240 px. Oculta por debajo de 901 px. */}
      <aside
        ref={lateralRef}
        className="hidden min-[901px]:fixed min-[901px]:inset-y-0 min-[901px]:left-0 min-[901px]:flex min-[901px]:w-60 min-[901px]:flex-col min-[901px]:overflow-y-auto min-[901px]:border-r min-[901px]:border-borde min-[901px]:z-30 min-[901px]:bg-superficie min-[901px]:px-3 min-[901px]:py-[22px]"
      >
        <Marca />

        <nav aria-label="Navegación principal" className="flex flex-col gap-0.5">
          <NavLink to={INICIO.ruta} className="tz-nav-item">
            <INICIO.Icono />
            <span>{INICIO.etiqueta}</span>
          </NavLink>

          {grupos.map((grupo) => {
            const activo = grupo === grupoActivo
            return (
              <div key={grupo.id} onBlur={(evento) => alPerderFoco(evento, grupo.id)}>
                <button {...propsBoton(grupo.id)} className={`tz-grupo${activo ? ' is-active' : ''}`}>
                  <grupo.Icono aria-hidden="true" />
                  <span className="tz-grupo-txt">
                    <span>{grupo.etiqueta}</span>
                    {activo && pantallaActiva ? <small>{pantallaActiva.etiqueta}</small> : null}
                  </span>
                  <IconoAbrirPanel aria-hidden="true" className="tz-grupo-chev" />
                </button>
                <div {...propsPanel(grupo.id, grupo.etiqueta)}>
                  <p className="tz-panel-h" aria-hidden="true">
                    <grupo.Icono />
                    {grupo.etiqueta}
                  </p>
                  {grupo.destinos.map((destino) => (
                    <Enlace key={destino.ruta} destino={destino} alElegir={() => cerrarPanel(false)} />
                  ))}
                </div>
              </div>
            )
          })}
        </nav>

        <div className="tz-sidebar-foot tz-pie-cuenta" onBlur={(evento) => alPerderFoco(evento, 'cuenta')}>
          <button {...propsBoton('cuenta')} className="tz-cuenta">
            <span className="tz-avatar" aria-hidden="true">
              {iniciales(nombre)}
            </span>
            <span className="tz-grupo-txt">
              <span className="sr-only">Cuenta: </span>
              <b>{nombre}</b>
              {rol ? <small>{rol}</small> : null}
            </span>
            <IconoAbrirPanel aria-hidden="true" className="tz-grupo-chev" />
          </button>
          <div {...propsPanel('cuenta', 'Cuenta')}>
            <p className="tz-panel-h" aria-hidden="true">
              <IconoUsuarios />
              Cuenta
            </p>
            <Cuenta correo={correo} salir={cerrarSesion} />
          </div>
        </div>
      </aside>

      {/* El hueco de la lateral se reserva en el envoltorio, no en el propio
          main: asi el ancho maximo del contenido se centra en el espacio que
          queda libre. pb-24 en movil: la barra inferior es fija y taparia la
          ultima fila. */}
      <div className="min-[901px]:pl-60">
        {/* Movil: tira superior con la marca. El tema y la cuenta van en «Más». */}
        <div className="flex min-h-14 items-center gap-2.5 border-b border-borde bg-superficie px-4 py-2 min-[901px]:hidden">
          <LogisticaIconoTema size={32} />
          <div className="min-w-0">
            <p className="tz-brand-name truncate text-base">Logística</p>
            <span className="tz-brand-sub mt-0.5">Panel de operación</span>
          </div>
        </div>

        <main className="mx-auto max-w-7xl px-4 pb-24 pt-6 min-[901px]:px-8 min-[901px]:pb-10">{children}</main>
      </div>

      {/* Movil: cinco celdas a partes iguales, sin desplazamiento lateral. Sin
          `aria-label`: el nombre accesible es el rotulo visible (WCAG 2.5.3). */}
      <nav
        aria-label="Navegación principal móvil"
        className="tz-barra-movil min-[901px]:hidden"
        style={{ gridTemplateColumns: `repeat(${gruposBarra.length + 2}, minmax(0, 1fr))` }}
      >
        <NavLink to={INICIO.ruta} className="tz-celda">
          <INICIO.Icono aria-hidden="true" />
          <span>{INICIO.etiquetaMovil}</span>
        </NavLink>
        {gruposBarra.map((grupo) => (
          <button
            key={grupo.id}
            type="button"
            className={`tz-celda${grupo === grupoActivo ? ' is-active' : ''}`}
            aria-expanded={hoja === grupo.id}
            aria-controls={ID_HOJA}
            onClick={(evento) => abrirHoja(grupo.id, evento.currentTarget)}
          >
            <grupo.Icono aria-hidden="true" />
            <span>{grupo.etiquetaMovil}</span>
          </button>
        ))}
        <button
          type="button"
          className={`tz-celda${masActivo ? ' is-active' : ''}`}
          aria-expanded={hoja === 'mas'}
          aria-controls={ID_HOJA}
          onClick={(evento) => abrirHoja('mas', evento.currentTarget)}
        >
          <IconoMas aria-hidden="true" />
          <span>Más</span>
        </button>
      </nav>

      {hoja ? (
        <div className="tz-hoja-capa min-[901px]:hidden">
          <div className="tz-hoja-fondo" aria-hidden="true" onClick={() => cerrarHoja(true)} />
          <div
            ref={hojaRef}
            id={ID_HOJA}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${ID_HOJA}-titulo`}
            className="tz-hoja"
            onKeyDown={alTeclearEnHoja}
          >
            <div className="tz-hoja-asa" aria-hidden="true" />
            <div className="tz-hoja-cab">
              <h2 id={`${ID_HOJA}-titulo`}>{tituloHoja}</h2>
              <button type="button" className="tz-icono-btn" aria-label="Cerrar" onClick={() => cerrarHoja(true)}>
                <IconoCerrar aria-hidden="true" />
              </button>
            </div>
            <nav aria-label={tituloHoja}>
              {hoja === 'mas' ? (
                <>
                  {gruposMas.map((grupo) => (
                    <div key={grupo.id} className="tz-hoja-seccion">
                      <p className="tz-nav-label">{grupo.etiqueta}</p>
                      {grupo.destinos.map((destino) => (
                        <Enlace key={destino.ruta} destino={destino} alElegir={() => cerrarHoja(false)} />
                      ))}
                    </div>
                  ))}
                  <div className="tz-hoja-seccion">
                    <p className="tz-nav-label">Cuenta</p>
                    <Cuenta correo={correo} salir={cerrarSesion} />
                  </div>
                </>
              ) : (
                grupoHoja?.destinos.map((destino) => (
                  <Enlace key={destino.ruta} destino={destino} alElegir={() => cerrarHoja(false)} />
                ))
              )}
            </nav>
          </div>
        </div>
      ) : null}
    </div>
  )
}
